import * as React from "react";
import { useContext, useState, FormEvent, useEffect } from "react";
import { useFetcher } from "@remix-run/react";
import compact from "lodash/compact";

import { action as requestPasswordlessAction } from "../../../../routes/auth.requestPasswordless";
import { action as verifyAction } from "../../../../routes/auth.verify";
import styles from "./AuthForm.module.css";
import Button from "../../../../components/Button/Button";
import Input from "../../../../components/Input/Input";
import SiteContext from "~/contexts/SiteContext";
import OrderContext from "~/contexts/OrderContext";
import { AuthMethod } from "~/entities/authEntities";
import AuthContext from "~/contexts/AuthContext";
import Tabs from "~/components/Tabs/Tabs";
import PhoneNumberInput from "~/components/PhoneNumberInput/PhoneNumberInput";
import CodeInput from "~/components/CodeInput/CodeInput";
import EditIcon from "~/icons/EditIcon";
import GoogleIcon from "~/icons/GoogleIcon";
import AppleIcon from "~/icons/AppleIcon";
import { isValidCode, validationEmail } from "~/helpers/validationHelpers";
import {
  FORM_FIELDS,
  LABELS,
  PLACEHOLDERS,
} from "~/modules/checkout/constants";

interface AuthFormProps {
  allowGuestCheckout: boolean;
  setAuthMethod: (authMethod: AuthMethod) => void;
  onComplete?: VoidFunction;
  setDisabledCloseDialog: (disabled: boolean) => void;
}

const AuthForm: React.FC<AuthFormProps> = ({
  allowGuestCheckout,
  setAuthMethod,
  onComplete,
  setDisabledCloseDialog,
}) => {
  const { customerSignInOptions, useComoAuth, comoRequiresSignup } =
    useContext(AuthContext);

  const CODE_LENGTH = useComoAuth && !comoRequiresSignup ? 4 : 6;
  const [codeSent, setCodeSent] = useState(false);
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [values, setValues] = useState<string[]>(Array(CODE_LENGTH).fill(""));
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [resendingLoading, setResendingLoading] = useState(false);
  const [verifyError, setVerifyError] = useState<string | null>(null);

  const { orgId, site } = useContext(SiteContext);
  const { orderSummary } = useContext(OrderContext);

  const fetcher = useFetcher<typeof requestPasswordlessAction>();
  const verifyFetcher = useFetcher<typeof verifyAction>();

  useEffect(() => {
    if (verifyFetcher.state === "idle") {
      if (verifyFetcher?.data?.success) {
        setVerifyError(null);
        onComplete && onComplete();
      }

      // Reset loading state if the verification is not successful so they may try again
      if (verifyFetcher.data && !verifyFetcher.data.success) {
        setVerifyLoading(false);
        setVerifyError(verifyFetcher.data.message);
        setValues(Array(CODE_LENGTH).fill(""));
      }
    }
  }, [CODE_LENGTH, onComplete, verifyFetcher.data, verifyFetcher.state]);

  useEffect(() => {
    if (fetcher?.data && "success" in fetcher.data) {
      setValues(Array(CODE_LENGTH).fill(""));
      setCodeSent(true);
      setDisabledCloseDialog(true);
    }
  }, [fetcher?.data, CODE_LENGTH, setDisabledCloseDialog]);

  const handleRequestCode = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const emailValue = formData.get("email")?.toString() ?? "";
    const phoneValue = formData.get("phone")?.toString() ?? "";
    setEmail(emailValue);
    setPhone(phoneValue);

    fetcher.submit(e.currentTarget);
  };

  const handleChangeContact = () => {
    setCodeSent(false);
    setDisabledCloseDialog(false);
    setEmail("");
    setPhone("");
  };

  const handleResendCode =
    (forceEmail: boolean) =>
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      e.preventDefault();

      // Prevent multiple rapid clicks
      if (resendingLoading) {
        return;
      }

      setResendingLoading(true);

      // Reset after 10 seconds
      const timer = setTimeout(() => {
        setResendingLoading(false);
      }, 10000);

      const formData = new FormData();
      formData.append("email", email);
      formData.append("phone", phone);
      formData.append("siteId", site.id);
      formData.append("orderId", orderSummary.id);
      formData.append("type", email ? "email" : "phone");
      if (forceEmail && !email) {
        formData.append("forceEmail", "true");
      }
      fetcher.submit(formData, {
        method: "post",
        action: "/auth/requestPasswordless",
      });

      return () => clearTimeout(timer);
    };

  const otherSignInAuthMethods: AuthMethod[] = compact([
    customerSignInOptions?.signInWith.google && AuthMethod.GOOGLE,
    customerSignInOptions?.signInWith.apple && AuthMethod.APPLE,
    allowGuestCheckout && AuthMethod.GUEST,
  ]);

  return (
    <div>
      {!codeSent ? (
        <fetcher.Form
          method="post"
          action="/auth/requestPasswordless"
          onSubmit={handleRequestCode}
          className={styles.form}
        >
          <input type="hidden" name="orgId" value={orgId} />
          <Tabs
            tabs={compact([
              useComoAuth && {
                label: "Enter phone",
                content: (
                  <>
                    <div className={styles.description}>
                      {LABELS.PLEASE_ENTER_YOUR_PHONE} {CODE_LENGTH}
                      {LABELS.DIGIT_CODE} {LABELS.WILL_BE_SENT}
                    </div>
                    <div className={styles.formGroup}>
                      <label htmlFor="phone">Phone</label>
                      <input type="hidden" name="phone" value={phone} />
                      <PhoneNumberInput
                        name={FORM_FIELDS.PHONE}
                        placeholder={PLACEHOLDERS.PHONE_NUMBER}
                        id="phone"
                        onChange={setPhone}
                        defaultCountry={site?.countryCode}
                        allowedCountryCodes={
                          customerSignInOptions?.smsAllowedCountryCodes
                        }
                        required
                      />
                    </div>
                  </>
                ),
              },
              customerSignInOptions?.passwordless.email && {
                label: "Enter email",
                content: (
                  <>
                    <div className={styles.description}>
                      {LABELS.PLEASE_ENTER_YOUR_EMAIL} {CODE_LENGTH}{" "}
                      {LABELS.DIGIT_CODE} {LABELS.WILL_BE_SENT}
                      {useComoAuth && " Only works for login, not signup."}
                    </div>
                    <div className={styles.formGroup}>
                      <label htmlFor="email">Email</label>
                      <Input
                        type="text"
                        id="email"
                        name="email"
                        required
                        onChange={(e) => {
                          e.currentTarget.setCustomValidity(
                            validationEmail(e.target.value)
                              ? ""
                              : "Please enter a valid email address",
                          );
                        }}
                      />
                    </div>
                  </>
                ),
              },
              !useComoAuth &&
                customerSignInOptions?.passwordless.sms && {
                  label: "Enter phone",
                  content: (
                    <>
                      <div className={styles.description}>
                        {LABELS.PLEASE_ENTER_YOUR_PHONE} {CODE_LENGTH}
                        {LABELS.DIGIT_CODE} {LABELS.WILL_BE_SENT}
                      </div>
                      <div className={styles.formGroup}>
                        <label htmlFor="phone">Phone</label>
                        <input type="hidden" name="phone" value={phone} />
                        <PhoneNumberInput
                          name={FORM_FIELDS.PHONE}
                          placeholder={PLACEHOLDERS.PHONE_NUMBER}
                          id="phone"
                          onChange={setPhone}
                          defaultCountry={site?.countryCode}
                          allowedCountryCodes={
                            customerSignInOptions?.smsAllowedCountryCodes
                          }
                          required
                        />
                      </div>
                    </>
                  ),
                },
            ])}
          />
          <div className={styles.buttonsContainer}>
            <Button loading={fetcher.state != "idle"} type="submit">
              Send Code
            </Button>
            {!!otherSignInAuthMethods.length && (
              <>
                <div className={styles.separator}>
                  <span>or</span>
                </div>
                {otherSignInAuthMethods.map((otherAuthMethod) => (
                  <React.Fragment key={otherAuthMethod}>
                    {otherAuthMethod === AuthMethod.GUEST && (
                      <Button
                        variant="secondary"
                        type="button"
                        onClick={() => setAuthMethod(AuthMethod.GUEST)}
                      >
                        Continue as Guest
                      </Button>
                    )}
                    {otherAuthMethod === AuthMethod.GOOGLE && (
                      <Button
                        variant="secondary"
                        type="button"
                        onClick={() => {
                          // ToDo: Not implemented...
                        }}
                      >
                        <GoogleIcon /> Continue with Google
                      </Button>
                    )}
                    {otherAuthMethod === AuthMethod.APPLE && (
                      <Button
                        variant="secondary"
                        type="button"
                        onClick={() => {
                          // ToDo: Not implemented...
                        }}
                      >
                        <AppleIcon /> Continue with Apple
                      </Button>
                    )}
                  </React.Fragment>
                ))}
              </>
            )}
          </div>
        </fetcher.Form>
      ) : (
        <verifyFetcher.Form
          method="post"
          action="/auth/verify"
          className={styles.form}
          onSubmit={() => {
            setVerifyLoading(true);
          }}
        >
          <input type="hidden" name="email" value={email} />
          <input type="hidden" name="phone" value={phone} />
          <input type="hidden" name="orgId" value={orgId} />
          <input type="hidden" name="siteId" value={site.id} />
          <input type="hidden" name="orderId" value={orderSummary.id} />
          <input type="hidden" name="type" value={email ? "email" : "phone"} />
          <div className={styles.description}>
            {LABELS.WE_SENT_A_CODE} {email ? "email" : "phone"}
            <div className={styles.receivedCodeContact}>
              {email || phone} <EditIcon onClick={handleChangeContact} />
            </div>
          </div>
          <div className={styles.formGroup}>
            <label htmlFor="code">Code</label>
            <CodeInput
              length={CODE_LENGTH}
              id="code"
              name="code"
              values={values}
              setValues={(newValues) => {
                setValues(newValues);
                setVerifyError(null);
              }}
            />
            <div className={styles.resendMessage}>
              {resendingLoading ? (
                <div className={styles.resending}>Resending code...</div>
              ) : (
                <>
                  Didn’t get it?{" "}
                  <div
                    className={styles.resendButton}
                    onClick={handleResendCode(false)}
                  >
                    Resend code
                  </div>
                  {useComoAuth && !email && (
                    <div
                      className={styles.resendButton}
                      onClick={handleResendCode(true)}
                    >
                      Send via email instead
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
          <div className={styles.buttonsContainer}>
            <Button
              loading={verifyLoading}
              type="submit"
              disabled={!isValidCode(values, CODE_LENGTH)}
            >
              Verify
            </Button>
          </div>
        </verifyFetcher.Form>
      )}
      {!!verifyError && (
        <div className={styles.errorMessage}>{verifyError}</div>
      )}
    </div>
  );
};

export default AuthForm;
